import React from 'react';

import Data from 'data';
import Store from 'store';
import Session from 'store/session';

import ReactMarkdown from 'react-markdown/with-html';
import Editor, { defaultToolbar, replaceSelection, getSelection } from './Editor';
  
import { BuildInfoLevel, getIconPath, getTypeIconsPath } from 'common';
import { plugins, renderers } from './renderer';

import './BuildInfo.scss';
import EditingContext from 'store/editingContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEyeSlash, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import Dialog from 'ui/dialog';
import { OverlayContext } from 'ui/utils/OverlayManager';
import Locale from 'data/locale';
import Select, { underlinerFunc } from 'ui/select';
import { D3TooltipBuff, D3TooltipFollowerSkill, D3TooltipItem, D3TooltipSet, D3TooltipSkill } from 'tooltips';

import { renderGemColorOption, renderGemTypeOption, searchGemType } from 'components/equipment/ItemEditor';
import AdUnit from 'AdUnit';

const _L = Locale();

function setText(profile, text) {
  if (profile.buildinfo?.text === text) {
    return profile;
  }
  profile = {...profile};
  profile.buildinfo = {...profile.buildinfo, text};
  return profile;
}

const canonicalName = name => name.replace(/[^\w]/g, "").toLowerCase();

const linkItem = {
  id: "item",
  name: "Item (Equipment)",
  valid1(id) {
    const type = id && Data.itemById[id]?.type;
    return type && Data.itemTypes[type];
  },
  first1: () => Data.items.find(item => !item.legacy).id,
  param1: {
    virtualScroll: true,
    searchLimit: 10,
    options() {
      const types = {};
      for (const item of Data.items) {
        if (item.legacy) continue;
        if (!types[item.type]) types[item.type] = [];
        types[item.type].push(item);
      }
      return Object.entries(types).map(([type, items]) => ({
        title: Data.itemTypes[type].name,
        options: items.sort((a, b) => a.name.localeCompare(b.name)).map(item => item.id),
      }));
    },
    renderValue: id => <span>{Data.itemById[id]?.name}</span>,
    renderOption(id, search) {
      const item = Data.itemById[id];
      if (!item) return null;

      const icon = Data.itemIcons[item.realid || id];
      return (
        <D3TooltipItem data={id}>
          <div className={`d3p-option-item d3p-quality-${item.quality}`}>
            {!!icon && <span className="d3p-option-icon" style={{backgroundImage: `url(${getTypeIconsPath(item.type)})`, backgroundPosition: `0 ${-icon[0]}em`}}/>}
            {!!item.icon && <span className="d3p-option-icon d3p-option-icon-custom" style={{backgroundImage: `url(${getIconPath(item.icon)})`}}/>}
            {search ? underlinerFunc(item.name, search) : item.name}
          </div>
        </D3TooltipItem>
      );
    },
    searchFunc(id, search) {
      const item = Data.itemById[id];
      if (!item) return false;
      if (item.name.match(search)) return true;
      if (item.required?.custom) {
        if (item.required.custom.format?.match(search)) return true;
        if (item.required.custom.options?.some(id => Data.itemById[id].required?.custom?.format.match(search))) return true;
      }
      return false;
    },
  },

  find(name) {
    name = canonicalName(name);
    const item = Data.items.find(item => canonicalName(item.name) === name);
    if (item) return [item.id];
  },
  parse(id) {
    const m = id.match(/^item\.(.*)$/);
    if (m && this.valid1(m[1])) return [m[1]];
  },
  result: id => ({id: `item.${id}`, name: Data.itemById[id]?.name || ""}),
};
const linkItemMisc = {
  id: "itemmisc",
  name: "Item (Other)",
  valid1(id) {
    const item = id && Data.itemById[id];
    return item && (item.type === "potion" || item.icon);
  },
  first1: () => Data.potions[0].id,
  param1: {
    virtualScroll: true,
    searchLimit: 10,
    options() {
      const types = {};
      for (const item of Data.potions) {
        if (!types[item.type]) types[item.type] = [];
        types[item.type].push(item);
      }
      for (const item of Data.extraItems) {
        if (!types[item.type || ""]) types[item.type || ""] = [];
        types[item.type || ""].push(item);
      }
      return Object.entries(types).map(([type, items]) => ({
        title: Data.itemTypes[type]?.name || Data.extraTypes[type] || _L`Misc`,
        options: items.sort((a, b) => a.name.localeCompare(b.name)).map(item => item.id),
      }));
    },
    renderValue: linkItem.param1.renderValue,
    renderOption: linkItem.param1.renderOption,
    searchFunc: linkItem.param1.searchFunc,
  },

  find(name) {
    name = canonicalName(name);
    const item = Data.potions.find(item => canonicalName(item.name) === name) ||
      Data.extraItems.find(item => canonicalName(item.name) === name);
    if (item) return [item.id];
  },
  parse(id) {
    const m = id.match(/^item\.(.*)$/);
    if (m && this.valid1(m[1])) return [m[1]];
  },
  result: id => ({id: `item.${id}`, name: Data.itemById[id]?.name || ""}),
};
const linkSet = {
  id: "set",
  name: "Item Set",
  valid1: id => Data.itemSets[id],
  first1: () => Object.keys(Data.itemSets).find(id => !Data.itemSets[id].legacy),
  param1: {
    virtualScroll: true,
    searchLimit: 10,
    options() {
      const classes = {};
      for (const [id, set] of Object.entries(Data.itemSets)) {
        if (set.legacy) continue;
        const cls = set.class || set.tclass || "";
        if (!classes[cls]) classes[cls] = [];
        classes[cls].push(id);
      }
      return ["", ...Object.keys(Data.classes)].filter(cls => !!classes[cls]).map(cls => ({
        title: Data.classes[cls]?.name || _L`All Classes`,
        options: classes[cls].sort((a, b) => Data.itemSets[a].name.localeCompare(Data.itemSets[b].name)),
      }));
    },
    renderValue: id => <span>{Data.itemSets[id]?.name}</span>,
    renderOption(id, search) {
      const set = Data.itemSets[id];
      if (!set) return null;
      return (
        <D3TooltipSet id={id}>
          <div className={`d3p-option-set d3p-quality-set`}>
            {search ? underlinerFunc(set.name, search) : set.name}
          </div>
        </D3TooltipSet>
      );
    },
    searchFunc: (id, search) => Data.itemSets[id]?.name.match(search),
  },

  find(name) {
    name = canonicalName(name);
    const id = Object.keys(Data.itemSets).find(id => canonicalName(Data.itemSets[id].name) === name);
    if (id) return [id];
  },
  parse(id) {
    const m = id.match(/^set\.(.*)$/);
    if (m && this.valid1(m[1])) return [m[1]];
  },
  result: id => ({id: `set.${id}`, name: Data.itemSets[id]?.name || ""}),
};
const linkGem = {
  id: "gem",
  name: "Gem",

  valid1: id => Data.legendaryGems[id] || Data.gemQualities[id],
  first1: () => Object.keys(Data.legendaryGems)[0],
  param1: {
    searchLimit: 11,
    options: () => [
      {
        title: _L`Legendary Gems`,
        options: Object.keys(Data.legendaryGems),
      },
      {
        title: _L`Normal Gems`,
        options: Object.keys(Data.gemQualities).reverse(),
      }
    ],
    renderValue: id => <span>{Data.legendaryGems[id]?.name || Data.gemQualities[id]}</span>,
    renderOption: (id, search) => renderGemTypeOption(id, search, 0),
    searchFunc: searchGemType,
  },

  valid2: (id, color) => Data.gemColors[color],
  first2: id => Object.keys(Data.gemColors)[0],
  param2: type => Data.gemQualities[type] && ({
    options: () => Object.keys(Data.gemColors),
    renderValue: id => <span>{Data.gemColors[id]?.name}</span>,
    renderOption: (id, search) => renderGemColorOption(id, search, type),
  }),

  find(name) {
    name = canonicalName(name);
    const id = Object.keys(Data.legendaryGems).find(id => canonicalName(Data.legendaryGems[id].name) === name);
    if (id) return [id];
    for (const color in Data.gemColors) {
      const index = Data.gemColors[color].names.indexOf(name);
      if (index >= 0) return [index, color];
    }
  },
  parse(id) {
    const m = id.match(/^item\.(.*)$/);
    return m && Data.gemById[m[1]];
  },
  result(id, color) {
    if (Data.legendaryGems[id]) {
      return {id: `item.${id}`, name: Data.legendaryGems[id].name};
    } else if (Data.gemQualities[id] && Data.gemColors[color]) {
      return {
        id: `item.${Data.gemColors[color].id + (parseInt(id) + 1).toString().padStart(2, '0')}`,
        name: Data.gemColors[color].names[id],
      };
    }
  },
};
const linkSkill = {
  id: "skill",
  name: "Skill",

  valid1: id => Data.allSkills[id],
  first1: () => Object.keys(Data.allSkills)[0],
  param1: {
    searchLimit: 10,
    virtualScroll: true,
    options: () => Object.keys(Data.classes).filter(cls => !Data.classes[cls].follower).map(cls => ({
      title: Data.classes[cls].name,
      options: Object.keys(Data.skills[cls]),
    })),
    renderValue: id => <span>{Data.allSkills[id]?.[1].name}</span>,
    renderOption(id, search) {
      const skill = Data.allSkills[id];
      if (!skill) return null;

      return (
        <D3TooltipSkill charClass={skill[0]} skill={id}>
          <div className={`d3p-option-skill`}>
            <span className={`d3p-option-icon d3p-skill-icon d3p-class-${skill[0]}`} style={{backgroundPosition: `${-skill[1].col}em ${-2 * skill[1].row}em`}}/>
            {search ? underlinerFunc(skill[1].name, search) : skill[1].name}
          </div>
        </D3TooltipSkill>
      );
    },
    searchFunc: (id, search) => Data.allSkills[id]?.name.match(search),
  },

  find(name) {
    name = canonicalName(name);
    const id = Object.keys(Data.allSkills).find(id => canonicalName(Data.allSkills[id][1].name) === name);
    if (id) return [id];
  },
  parse(id) {
    const m = id.match(/^skill\.(.*)\.(.*)$/);
    if (m && Data.skills[m[1]]?.[m[2]]) {
      return [m[2]];
    }
  },
  result(id) {
    const skill = Data.allSkills[id];
    if (skill) {
      return {id: `skill.${skill[0]}.${id}`, name: skill[1].name};
    }
  },
};
const linkRune = {
  id: "rune",
  name: "Rune",

  valid1: linkSkill.valid1,
  first1: linkSkill.first1,
  param1: linkSkill.param1,

  valid2: (id, rune) => Data.allSkills[id]?.[1].runes[rune],
  first2: id => Object.keys(Data.allSkills[id]?.[1].runes || {})[0],
  param2: id => Data.allSkills[id] && ({
    options: () => Object.keys(Data.allSkills[id][1].runes),
    renderValue: rune => <span>{Data.allSkills[id][1].runes[rune]}</span>,
    renderOption(rune) {
      const skill = Data.allSkills[id];
      if (!skill || !skill[1].runes[rune]) return null;
      return (
        <D3TooltipSkill charClass={skill[0]} skill={id} rune={rune} onlyRune>
          <div className={`d3p-option-rune`}>
            <span className={`d3p-option-icon d3p-rune d3p-rune-${rune}`}/>
            {skill[1].runes[rune]}
          </div>
        </D3TooltipSkill>
      );
    },
  }),

  find(name) {
    name = canonicalName(name);
    for (const id in Data.allSkills) {
      const runes = Data.allSkills[id][1].runes;
      for (const rune in runes) {
        if (canonicalName(runes[rune]) === name) {
          return [id, rune];
        }
      }
    }
  },
  parse(id) {
    const m = id.match(/^rune\.(.*)\.(.*)$\.(.*)/);
    if (m && Data.skills[m[1]]?.[m[2]]?.runes[m[3]]) {
      return [m[2], m[3]];
    }
  },
  result(id, rune) {
    const skill = Data.allSkills[id];
    if (skill) {
      return {id: `rune.${skill[0]}.${id}.${rune}`, name: skill[1].runes[rune]};
    }
  },
};
const linkSkillRune = {
  id: "skill-rune",
  name: "Skill + Rune",

  valid1: linkSkill.valid1,
  first1: linkSkill.first1,
  param1: linkSkill.param1,

  valid2: linkRune.valid2,
  first2: linkRune.first2,
  param2: linkRune.param2,

  find(name) {
    name = canonicalName(name);
    for (const id in Data.allSkills) {
      const skill = canonicalName(Data.allSkills[id][1].name);
      const pos = name.indexOf(skill);
      if (pos < 0) continue;
      const rest = name.substring(pos + skill.length);
      const runes = Data.allSkills[id][1].runes;
      for (const rune in runes) {
        if (rest.indexOf(canonicalName(runes[rune])) >= 0) {
          return [id, rune];
        }
      }
    }
  },
  parse(id) {
    const m = id.match(/^skill-rune\.(.*)\.(.*)\.(.*)$/);
    if (m && Data.skills[m[1]]?.[m[2]]?.runes[m[3]]) {
      return [m[2], m[3]];
    }
  },
  result(id, rune) {
    const skill = Data.allSkills[id];
    if (skill) {
      return {id: `skill-rune.${skill[0]}.${id}.${rune}`, name: `${skill[1].name} ${skill[1].runes[rune]}`};
    }
  },
};
const linkPassive = {
  id: "passive",
  name: "Passive",

  valid1: id => Data.allPassives[id],
  first1: () => Object.keys(Data.allPassives)[0],
  param1: {
    searchLimit: 10,
    virtualScroll: true,
    options: () => Object.keys(Data.classes).filter(cls => !Data.classes[cls].follower).map(cls => ({
      title: Data.classes[cls].name,
      options: Object.keys(Data.passives[cls]),
    })),
    renderValue: id => <span>{Data.allPassives[id]?.[1].name}</span>,
    renderOption(id, search) {
      const passive = Data.allPassives[id];
      if (!passive) return null;

      return (
        <D3TooltipSkill charClass={passive[0]} skill={id}>
          <div className={`d3p-option-passive`}>
            <span className={`d3p-option-icon d3p-passive-icon d3p-class-${passive[0]}`} style={{backgroundPosition: `${-passive[1].index}em 0`}}/>
            {search ? underlinerFunc(passive[1].name, search) : passive[1].name}
          </div>
        </D3TooltipSkill>
      );
    },
    searchFunc: (id, search) => Data.allPassives[id]?.name.match(search),
  },

  find(name) {
    name = canonicalName(name);
    const id = Object.keys(Data.allPassives).find(id => canonicalName(Data.allPassives[id][1].name) === name);
    if (id) return [id];
  },
  parse(id) {
    const m = id.match(/^passive\.(.*)\.(.*)$/);
    if (m && Data.passives[m[1]]?.[m[2]]) {
      return [m[2]];
    }
  },
  result(id) {
    const passive = Data.allPassives[id];
    if (passive) {
      return {id: `passive.${passive[0]}.${id}`, name: passive[1].name};
    }
  },
};

const linkBuff = {
  id: "buff",
  name: "Buff",

  valid1: id => Data.shrineBuffs[id] && id.match(/^(?:shrine|pylon)_/),
  first1: () => Object.keys(Data.shrineBuffs).find(linkBuff.valid1),
  param1: {
    searchLimit: 10,
    options: () => [{
      title: _L`Shrines`,
      options: Object.keys(Data.shrineBuffs).filter(id => id.match(/^shrine_/)),
    }, {
      title: _L`Pylons`,
      options: Object.keys(Data.shrineBuffs).filter(id => id.match(/^pylon_/)),
    }],
    renderValue: id => <span>{Data.shrineBuffs[id]?.name}</span>,
    renderOption(id, search) {
      const buff = Data.shrineBuffs[id];
      if (!buff) return null;

      return (
        <D3TooltipBuff id={id}>
          <div className={`d3p-option-buff`}>
            <span className={`d3p-option-icon d3p-option-icon-special`} style={{backgroundImage: `url(${getIconPath(buff.icon)})`}}/>
            {search ? underlinerFunc(buff.name, search) : buff.name}
          </div>
        </D3TooltipBuff>
      );
    },
    searchFunc: (id, search) => Data.shrineBuffs[id]?.name.match(search),
  },

  find(name) {
    name = canonicalName(name);
    const id = Object.keys(Data.shrineBuffs).find(id => canonicalName(Data.shrineBuffs[id].name) === name || canonicalName(Data.shrineBuffs[id].altname) === name);
    if (id) return [id];
  },
  parse(id) {
    const m = id.match(/^buff\.(.*)$/);
    if (m && Data.shrineBuffs[m[1]]) {
      return [m[1]];
    }
  },
  result(id) {
    const buff = Data.shrineBuffs[id];
    if (buff) {
      return {id: `buff.${id}`, name: buff.name};
    }
  },
};

const linkFollower = {
  id: "follower",
  name: "Follower Skill",

  valid1: id => Data.followerSkills[id],
  first1: () => Object.keys(Data.followerSkills)[0],
  param1: {
    searchLimit: 10,
    options: () => Object.keys(Data.classes).filter(cls => Data.classes[cls].follower).map(cls => ({
      title: Data.classes[cls].name,
      options: Object.keys(Data.followerSkills).filter(id => Data.followerSkills[id].class === cls),
    })),
    renderValue: id => <span>{Data.followerSkills[id]?.name}</span>,
    renderOption(id, search) {
      const skill = Data.followerSkills[id];
      if (!skill) return null;

      return (
        <D3TooltipFollowerSkill id={id}>
          <div className={`d3p-option-skill`}>
            <span className={`d3p-option-icon d3p-option-icon-special`} style={{backgroundImage: `url(${getIconPath(skill.icon)})`}}/>
            {search ? underlinerFunc(skill.name, search) : skill.name}
          </div>
        </D3TooltipFollowerSkill>
      );
    },
    searchFunc: (id, search) => Data.followerSkills[id]?.name.match(search),
  },

  find(name) {
    name = canonicalName(name);
    const id = Object.keys(Data.followerSkills).find(id => canonicalName(Data.followerSkills[id].name) === name);
    if (id) return [id];
  },
  parse(id) {
    const m = id.match(/^follower\.(.*)$/);
    if (m && Data.followerSkills[m[1]]) {
      return [m[1]];
    }
  },
  result(id) {
    const skill = Data.followerSkills[id];
    if (skill) {
      return {id: `follower.${id}`, name: skill.name};
    }
  },
};

const linkTypes = [linkItem, linkItemMisc, linkSet, linkGem, linkSkill, linkRune, linkSkillRune, linkPassive, linkBuff, linkFollower];

function LinkDialog({ defaultValue, onResolve, onClose }) {
  const [type, setType] = React.useState("item");
  const [idOpt, setId] = React.useState();
  const [id2Opt, setId2] = React.useState();
  const [source, setSource] = React.useState();

  const onChangeType = React.useCallback(opt => setType(opt.id), []);

  const params = linkTypes.find(opt => opt.id === type);

  const id = params.valid1(idOpt) ? idOpt : params.first1();
  React.useLayoutEffect(() => {
    if (idOpt !== id) {
      setId(id);
    }
  }, [id, idOpt]);

  const params2 = React.useMemo(() => params.param2?.(id), [params, id]);
  
  const id2 = (!params.valid2 || params.valid2(id, id2Opt)) ? id2Opt : params.first2(id);
  React.useLayoutEffect(() => {
    if (id2Opt !== id2) {
      setId2(id2);
    }
  }, [id2, id2Opt]);

  React.useEffect(() => {
    const m = defaultValue.match(/^(.*)\[(.*)\]\((.*)\)(.*)$/);
    for (const param of linkTypes) {
      const parsed = m && param.parse(m[3]);
      const found = parsed || param.find(defaultValue);
      if (found) {
        setType(param.id);
        setId(found[0]);
        setId2(found[1]);
        if (parsed) setSource(m);
        break;
      }
    }
  }, [defaultValue]);

  const onAccept = React.useCallback(() => {
    const result = params.result(id, id2);
    if (source) {
      result.prefix = source[1];
      result.suffix = source[4];
    }
    onResolve(result);
  }, [onResolve, params, id, id2, source]);

  return (
    <Dialog className="d3p-LinkDialog" modal title="Insert Link" onClose={onClose}>
      <Dialog.Content>
        <Select options={linkTypes} value={params} onChange={onChangeType}/>
        {!!params && <Select {...params.param1} value={id} onChange={setId}/>}
        {!!params2 && <Select {...params2} value={id2} onChange={setId2}/>}
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={onAccept}>{_L`Insert`}</button>
        <button onClick={onClose}>{_L`Cancel`}</button>
      </Dialog.Footer>
    </Dialog>
  );
}

const mdOptions = {renderers, plugins};

export default React.memo(function BuildInfo() {
  const [editing, setEditing] = EditingContext.useState("buildInfoEditing", true);
  const canEdit = Session.useSelector(store => (store.userLevel || 0) >= BuildInfoLevel);
  const text = Store.useProfile(profile => profile.buildinfo?.text || "");

  const dispatch = Store.useDispatch();
  const onChange = React.useCallback(value => dispatch.setProfile(profile => setText(profile, value)), [dispatch]);

  const overlays = React.useContext(OverlayContext);

  React.useEffect(() => {
    if (canEdit && !text && !editing) {
      setEditing(true);
    }
  }, [canEdit, editing, text, setEditing]);

  const onEdit = React.useCallback(() => setEditing(true), [setEditing]);

  const commands = React.useMemo(() => {
    const commands = [...defaultToolbar];
    commands.push({
      title: 'Item Link',
      icon: <FontAwesomeIcon icon={faShieldAlt}/>,
      hotkey: 'Ctrl+D',
      async action(editor) {
        const sel = getSelection(editor.current);
        const item = await overlays.async(<LinkDialog defaultValue={sel.text}/>);
        if (item) {
          replaceSelection(editor.current, sel, `${item.prefix || ""}[${item.name}](${item.id})${item.suffix || ""}`,
            1 + (item.prefix?.length || 0), 1 + item.name.length + (item.prefix?.length || 0));
        }
      },
    });
    if (text.length) {
      commands.push({
        name: 'Hide',
        icon: <FontAwesomeIcon icon={faEyeSlash}/>,
        className: "d3p-float-right",
        action() {
          setEditing(false);
        },
      });
    }
    return commands;
  }, [overlays, text, setEditing]);

  return <>
    <div className="d3p-TabContent d3p-BuildInfo">
      {canEdit && editing && (
        <Editor value={text} onChange={onChange} toolbar={commands}/>
      )}
      {canEdit && !editing && (
        <div className="d3md-edit" onClick={onEdit}>
          <FontAwesomeIcon icon={faEdit}/>
        </div>
      )}
      <div className="d3md-preview">
        <ReactMarkdown {...mdOptions} children={text} allowDangerousHtml/>
      </div>
    </div>
    <AdUnit id="d3planner-tab-guide" className="d3p-TabFooter d3planner-tab-ad"/>
  </>;
});
